import { Link } from 'react-router-dom';
import { LockClosedIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import type { ClaimableCamp } from '../data/campClaimSeedData';

interface PurchaseTeaserProps {
  camp: ClaimableCamp;
  currentUserId?: string;
}

/**
 * PurchaseTeaser Component
 * 
 * Shows a teaser banner for camp owners who have claimed their camp 
 * but haven't completed the purchase yet. Motivates them to finish 
 * the setup process to unlock all features.
 * 
 * Only shown when:
 * - Current user is the owner of the camp
 * - Camp is claimed but not paid (no payment_status or payment_status !== 'completed')
 */
export default function PurchaseTeaser({ camp, currentUserId }: PurchaseTeaserProps) {
  // Only show if current user is the owner
  if (!currentUserId || camp.owner_id !== currentUserId) {
    return null;
  }

  // Only show if camp is claimed but payment is not completed (half-claimed)
  const hasCompletedPayment = camp.claimed && camp.plan_id;
  if (!camp.claimed || hasCompletedPayment) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-6 mb-6 shadow-md max-w-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Need to edit info? Pick one of our plans to unlock full access!
      </h3>
      <p className="text-sm text-gray-700 mb-4 leading-relaxed">
        You've claimed <strong>{camp.name}</strong>! Complete your setup to unlock:
      </p>
      
      <ul className="text-sm text-gray-700 space-y-1 mb-4">
        <li className="flex items-center">
          <span className="text-green-500 mr-2">✓</span>
          Full camp management dashboard
        </li>
        <li className="flex items-center">
          <span className="text-green-500 mr-2">✓</span>
          Premium listing features
        </li>
        <li className="flex items-center">
          <span className="text-green-500 mr-2">✓</span>
          Customer contact management
        </li>
        <li className="flex items-center">
          <span className="text-green-500 mr-2">✓</span>
          Analytics and insights
        </li>
      </ul>
      
      <div className="flex flex-col space-y-3">
        <Link
          to="/pricing/owners"
          className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
        >
          Complete Setup
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </Link>
        
        <Link
          to="#"
          className="inline-flex items-center justify-center text-sm font-medium text-orange-700 hover:text-orange-800 underline transition-colors"
          onClick={(e) => e.preventDefault()}
        >
          Learn More
        </Link>
      </div>
      
      <div className="mt-4 text-xs text-orange-600">
        <LockClosedIcon className="inline h-4 w-4 mr-1" />
        Your camp is safely reserved while you complete the setup process.
      </div>
    </div>
  );
}